import React, { useEffect, useMemo, useState } from "react";
import { FEEDS_SPEEDS_OPERATIONS, calculateFeedsSpeeds } from "../../utils/calculations";
import {
  getMaterialLabel,
  TOOL_COATINGS,
  TOOL_MATERIAL_OPTIONS,
} from "../../data/machiningMaterials";
import MaterialSelector from "../MaterialSelector";
import ToolCoatingSelector from "../ToolCoatingSelector";
import UnitSystemSelector from "../UnitSystemSelector";
import useCalculatorMode from "../../hooks/useCalculatorMode";
import useCalculatorResults from "../../hooks/useCalculatorResults";
import CalculationImportPanel from "../CalculationImportPanel";
import { getRecords } from "../../utils/shopDataStore";

const MACHINE_TYPES = {
  cnc_mill: "CNC Mill",
  cnc_lathe: "CNC Lathe",
  manual_mill: "Manual Mill",
  manual_lathe: "Manual Lathe",
  drill_press: "Drill Press",
};

const FeedsSpeedsCalculator = ({ onSave }) => {
  const [params, setParams] = useState({
    unitSystem: "imperial",
    machineType: "cnc_mill",
    operation: "side_milling",
    material: "mild_steel",
    toolMaterial: "carbide",
    toolCoating: "uncoated",
    toolDiameter: 0.5,
    stockDiameter: 1.5,
    flutes: 2,
    chipLoad: 0.005,
    sfmOverride: "",
    feedOverride: "",
    machineMaxRPM: "",
    machineHorsepower: "",
    rigidityFactor: 1,
    stickout: "",
    radialEngagement: "",
    coolantMode: "flood",
    shopToolId: "",
    shopMachineId: "",
  });
  const [results, setResults] = useState(null);
  const [errors, setErrors] = useState({});
  const { isAdvanced, mode, toggleMode } = useCalculatorMode();
  const { saveResult } = useCalculatorResults("feeds-speeds", params.unitSystem);
  const shopTools = useMemo(() => getRecords("tools").filter((tool) => tool.status !== "Inactive"), []);
  const shopMachines = useMemo(() => getRecords("machines").filter((machine) => machine.status !== "Inactive"), []);

  const isMetric = params.unitSystem === "metric";
  const isLathe = ["cnc_lathe", "manual_lathe"].includes(params.machineType);
  const operationOptions = FEEDS_SPEEDS_OPERATIONS[params.machineType] || [];
  const selectedOperation = operationOptions.find((operation) => operation.id === params.operation) || operationOptions[0];
  const usesChipLoad = selectedOperation?.feedMode === "chip_load";
  const usesStockDiameter = selectedOperation?.diameterBasis === "stock";
  const diameterLabel = usesStockDiameter
    ? "Stock / Work Diameter"
    : usesChipLoad
      ? "Tool Diameter"
      : isLathe
        ? "Drill / Cutting Diameter"
        : "Tool / Cutting Diameter";
  const lengthUnit = isMetric ? "mm" : "inches";
  const feedUnit = usesChipLoad ? (isMetric ? "mm/min" : "IPM") : (isMetric ? "mm/rev" : "in/rev");
  const chipUnit = usesChipLoad ? (isMetric ? "mm/tooth" : "in/tooth") : (isMetric ? "mm/rev" : "in/rev");
  const speedUnit = isMetric ? "m/min" : "SFM";
  const displaySurfaceSpeed = (value) => isMetric ? Math.round(value * 0.3048) : value;
  const displayFeed = (value) => isMetric
    ? Math.round(value * 25.4 * (usesChipLoad ? 100 : 10000)) / (usesChipLoad ? 100 : 10000)
    : value;
  const displayChip = (value) => isMetric ? Math.round(value * 25.4 * 10000) / 10000 : value;

  useEffect(() => {
    const calcParams = {
      ...params,
      toolDiameter:
        params.unitSystem === "metric" ? params.toolDiameter / 25.4 : params.toolDiameter,
      stockDiameter:
        params.unitSystem === "metric" ? params.stockDiameter / 25.4 : params.stockDiameter,
      chipLoad: params.unitSystem === "metric" ? params.chipLoad / 25.4 : params.chipLoad,
      sfmOverride: params.sfmOverride,
      feedOverride: params.unitSystem === "metric" && params.feedOverride ? params.feedOverride / 25.4 : params.feedOverride,
      machineMaxRPM: params.machineMaxRPM,
      machineHorsepower: params.machineHorsepower,
      rigidityFactor: params.rigidityFactor,
      stickout: params.unitSystem === "metric" && params.stickout ? params.stickout / 25.4 : params.stickout,
      radialEngagement: params.unitSystem === "metric" && params.radialEngagement ? params.radialEngagement / 25.4 : params.radialEngagement,
      coolantMode: params.coolantMode,
    };
    const calcResults = calculateFeedsSpeeds(calcParams);
    if (calcResults.errors) {
      setErrors(calcResults.errors);
      setResults(null);
      return;
    }

    setErrors({});
    setResults(calcResults);
  }, [params]);

  useEffect(() => {
    if (!results) return;
    saveResult({
      calculatorId: "feeds-speeds",
      calculatorName: "Feeds, Speeds & RPM",
      inputValues: {
        ...params,
        material: getMaterialLabel(params.material),
        operation: selectedOperation?.label || params.operation,
      },
      outputValues: {
        rpm: results.optimalRPM,
        feedRate: results.feedRate,
        feedPerMinute: results.feedPerMinute,
        machineFeed: usesChipLoad ? results.feedRate : results.feedRate,
        toolDiameter: params.toolDiameter,
        stockDiameter: params.stockDiameter,
      },
      units: params.unitSystem,
      material: params.material,
      toolData: {
        toolMaterial: params.toolMaterial,
        toolCoating: params.toolCoating,
        toolDiameter: params.toolDiameter,
        stockDiameter: params.stockDiameter,
      },
      operationType: params.operation,
      warnings: results.warnings || [],
      sourceFormulaReferences: results.formulas || [],
    });
  }, [results, params, selectedOperation, usesChipLoad, saveResult]);

  const handleInputChange = (field, value) => {
    setParams((previous) => ({
      ...previous,
      [field]: ["material", "machineType", "operation", "toolMaterial", "toolCoating", "unitSystem"].includes(field)
        ? value
        : value === "" ? "" : parseFloat(value),
    }));
  };

  const handleMachineChange = (machineType) => {
    const defaultOperation = FEEDS_SPEEDS_OPERATIONS[machineType]?.[0]?.id || "";
    setParams((previous) => ({
      ...previous,
      machineType,
      operation: defaultOperation,
    }));
  };

  const resetForm = () => {
    setParams({
      unitSystem: "imperial",
      machineType: "cnc_mill",
      operation: "side_milling",
      material: "mild_steel",
      toolMaterial: "carbide",
      toolCoating: "uncoated",
      toolDiameter: 0.5,
      stockDiameter: 1.5,
      flutes: 2,
      chipLoad: 0.005,
      sfmOverride: "",
      feedOverride: "",
      machineMaxRPM: "",
      machineHorsepower: "",
      rigidityFactor: 1,
      stickout: "",
      radialEngagement: "",
      coolantMode: "flood",
      shopToolId: "",
      shopMachineId: "",
    });
  };

  const selectShopTool = (toolId) => {
    const tool = shopTools.find((item) => item.id === toolId);
    const materialMap = {
      "HSS": "hss",
      "Cobalt": "cobalt",
      "Solid carbide": "solid_carbide",
      "Indexable carbide": "indexable_carbide",
      "Brazed carbide": "brazed_carbide",
    };
    setParams((previous) => ({
      ...previous,
      shopToolId: toolId,
      toolDiameter: tool?.diameter || previous.toolDiameter,
      flutes: tool?.fluteCount || tool?.edgeCount || previous.flutes,
      stickout: tool?.stickout || previous.stickout,
      toolMaterial: materialMap[tool?.toolMaterial] || previous.toolMaterial,
    }));
  };

  const selectShopMachine = (machineId) => {
    const machine = shopMachines.find((item) => item.id === machineId);
    setParams((previous) => ({
      ...previous,
      shopMachineId: machineId,
      machineMaxRPM: machine?.spindleRpm || previous.machineMaxRPM,
      machineHorsepower: machine?.horsepower || previous.machineHorsepower,
    }));
  };

  const handleSave = () => {
    if (!results || !onSave) return;
    const displayedFeedRate = params.unitSystem === "metric"
      ? Math.round(results.feedRate * 25.4 * (usesChipLoad ? 100 : 10000)) / (usesChipLoad ? 100 : 10000)
      : results.feedRate;

    onSave({
      summary: `${getMaterialLabel(params.material)} | ${MACHINE_TYPES[params.machineType]} | ${selectedOperation?.label || "Operation"} | ${displayedFeedRate} ${feedUnit} @ ${results.optimalRPM} RPM`,
      input: {
        units: params.unitSystem,
        machineType: MACHINE_TYPES[params.machineType],
        operation: selectedOperation?.label || params.operation,
        material: getMaterialLabel(params.material),
        toolMaterial: TOOL_MATERIAL_OPTIONS[params.toolMaterial]?.label,
        toolCoating: TOOL_COATINGS[params.toolCoating]?.label,
        diameterForRPM: usesStockDiameter ? params.stockDiameter : params.toolDiameter,
        diameterType: results.rpmDiameterLabel || diameterLabel,
        flutes: usesChipLoad ? params.flutes : "N/A for feed per revolution operation",
        spindleRPM: results.optimalRPM,
        calculatedFeedPerRevOrChipLoad: displayedFeedRate,
      },
      results,
      warnings: results.warnings || [],
      confidence: results.confidence || "N/A",
    });
  };

  const copyToClipboard = () => {
    if (!results) return;
    const lengthUnit = params.unitSystem === "metric" ? "mm" : "in";
    const speedUnit = params.unitSystem === "metric" ? "m/min" : "SFM";
    const displayedSurfaceSpeed = params.unitSystem === "metric"
      ? Math.round(results.surfaceSpeed * 0.3048)
      : results.surfaceSpeed;
    const displayedFeedRate = params.unitSystem === "metric"
      ? Math.round(results.feedRate * 25.4 * (usesChipLoad ? 100 : 10000)) / (usesChipLoad ? 100 : 10000)
      : results.feedRate;
    const displayedOptimalFeed = params.unitSystem === "metric"
      ? Math.round(results.optimalFeed * 25.4 * (usesChipLoad ? 100 : 10000)) / (usesChipLoad ? 100 : 10000)
      : results.optimalFeed;
    const displayedBaselineSFM = params.unitSystem === "metric"
      ? Math.round(results.baselineSFM * 0.3048)
      : results.baselineSFM;
    const displayedBaselineChip = params.unitSystem === "metric"
      ? Math.round(results.baselineChipLoad * 25.4 * 10000) / 10000
      : results.baselineChipLoad;

    navigator.clipboard.writeText(
      [
        "ManuCalc - Feeds & Speeds",
        `Units: ${params.unitSystem === "metric" ? "Metric" : "Imperial"}`,
        `Machine: ${MACHINE_TYPES[params.machineType]}`,
        `Operation: ${selectedOperation?.label || params.operation}`,
        `Material: ${getMaterialLabel(params.material)}`,
        `Tool Material: ${TOOL_MATERIAL_OPTIONS[params.toolMaterial]?.label || params.toolMaterial}`,
        `Tool Coating: ${TOOL_COATINGS[params.toolCoating]?.label || params.toolCoating}`,
        `${results.rpmDiameterLabel || diameterLabel}: ${usesStockDiameter ? params.stockDiameter : params.toolDiameter} ${lengthUnit}`,
        usesChipLoad ? `Flutes: ${params.flutes}` : "Flutes/Inserts: not used for feed per revolution operation",
        `${usesChipLoad ? "Chip Load" : "Calculated Feed per Revolution"}: ${usesChipLoad ? params.chipLoad : displayedFeedRate} ${chipUnit}`,
        `Surface Speed: ${displayedSurfaceSpeed} ${speedUnit}`,
        `Feed Rate: ${displayedFeedRate} ${feedUnit}`,
        `Recommended RPM: ${results.optimalRPM}`,
        `Recommended Feed: ${displayedOptimalFeed} ${feedUnit}`,
        `Baseline Surface Speed: ${displayedBaselineSFM} ${speedUnit}`,
        `Baseline Chip Load: ${displayedBaselineChip} ${chipUnit}`,
      ].join("\n")
    );
  };

  return (
    <div className="calculator-page">
      <div className="calculator-shell">
        <div className="calculator-header" style={{ marginBottom: "24px" }}>
          <h1 style={{ color: "var(--text-primary)", marginBottom: "8px" }}>
            Feeds, Speeds & RPM Calculator
          </h1>
          <p style={{ color: "var(--text-muted)", margin: 0 }}>
            Select the actual operation being done, then calculate recommended RPM,
            SFM, feed per revolution, chip load, and feedrate starting values
            for mills, lathes, drill presses, and tailstock drilling.
          </p>
        </div>

        <div className="calculator-layout calculator-layout-wide-results">
          <div className="calculator-panel">
            <div className="calculator-mode-row">
              <span>{mode === "advanced" ? "Advanced Mode" : "Beginner Mode"}</span>
              <button type="button" className="btn-secondary" onClick={toggleMode}>
                {isAdvanced ? "Use Beginner Mode" : "Use Advanced Mode"}
              </button>
            </div>
            <CalculationImportPanel
              targetCalculatorId="feeds-speeds"
              targetUnits={params.unitSystem}
              onApply={({ values, lineage }) => {
                setParams((previous) => ({ ...previous, ...values, lineage }));
              }}
            />
            <div className="form-section" style={{ marginBottom: "24px" }}>
              <h3 style={{ color: "var(--text-primary)", marginBottom: "16px" }}>
                Machine, Material & Tool
              </h3>

              <UnitSystemSelector
                value={params.unitSystem}
                onChange={(value) => handleInputChange("unitSystem", value)}
              />

              {shopTools.length > 0 && (
                <div className="form-group">
                  <label>Tool Library Tool</label>
                  <select value={params.shopToolId} onChange={(event) => selectShopTool(event.target.value)}>
                    <option value="">Manual tool inputs</option>
                    {shopTools.map((tool) => <option key={tool.id} value={tool.id}>{tool.name}</option>)}
                  </select>
                </div>
              )}

              {shopMachines.length > 0 && (
                <div className="form-group">
                  <label>Shop Machine</label>
                  <select value={params.shopMachineId} onChange={(event) => selectShopMachine(event.target.value)}>
                    <option value="">No shop machine limit</option>
                    {shopMachines.map((machine) => <option key={machine.id} value={machine.id}>{machine.name}</option>)}
                  </select>
                </div>
              )}

              <div className="form-group">
                <label>Machine Type</label>
                <select
                  value={params.machineType}
                  onChange={(event) => handleMachineChange(event.target.value)}
                >
                  {Object.entries(MACHINE_TYPES).map(([key, label]) => (
                    <option key={key} value={key}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Operation</label>
                <select
                  value={params.operation}
                  onChange={(event) => handleInputChange("operation", event.target.value)}
                >
                  {operationOptions.map((operation) => (
                    <option key={operation.id} value={operation.id}>
                      {operation.label}
                    </option>
                  ))}
                </select>
              </div>

              <MaterialSelector
                value={params.material}
                onChange={(value) => handleInputChange("material", value)}
                error={errors.material}
              />

              <div className="form-group">
                <label>Tool Material</label>
                <select
                  value={params.toolMaterial}
                  onChange={(event) => handleInputChange("toolMaterial", event.target.value)}
                  className={errors.toolMaterial ? "error" : ""}
                >
                  {Object.entries(TOOL_MATERIAL_OPTIONS).map(([key, tool]) => (
                    <option key={key} value={key}>
                      {tool.label}
                    </option>
                  ))}
                </select>
              </div>

              <ToolCoatingSelector
                value={params.toolCoating}
                onChange={(value) => handleInputChange("toolCoating", value)}
                error={errors.toolCoating}
              />

              <div className="form-group">
                <label>{diameterLabel} ({lengthUnit})</label>
                <input
                  type="number"
                  step="0.001"
                  value={usesStockDiameter ? params.stockDiameter : params.toolDiameter}
                  onChange={(event) => handleInputChange(usesStockDiameter ? "stockDiameter" : "toolDiameter", event.target.value)}
                  className={usesStockDiameter ? errors.stockDiameter ? "error" : "" : errors.toolDiameter ? "error" : ""}
                />
              </div>

              {usesChipLoad && (
                <div className="form-group">
                  <label>Number of Flutes</label>
                  <input
                    type="number"
                    min="1"
                    max="20"
                    value={params.flutes}
                    onChange={(event) => handleInputChange("flutes", event.target.value)}
                    className={errors.flutes ? "error" : ""}
                  />
                </div>
              )}

              {usesChipLoad && (
                <div className="form-group">
                  <label>Chip Load per Tooth ({chipUnit})</label>
                  <input
                    type="number"
                    step="0.0001"
                    value={params.chipLoad}
                    onChange={(event) => handleInputChange("chipLoad", event.target.value)}
                    className={errors.chipLoad ? "error" : ""}
                  />
                </div>
              )}

              {!usesChipLoad && (
                <div style={noteBoxStyle}>
                  <strong>Feed per revolution is calculated.</strong> ManuCalc uses the
                  selected operation, material, tool material, coating, machine
                  type, and cutting diameter to estimate feed per revolution.
                  Use it as a starting point and adjust for tool geometry,
                  rigidity, finish, coolant, and chip control.
                </div>
              )}

              {isAdvanced && (
                <div className="advanced-options-panel">
                  <h3>Advanced Cutting Assumptions</h3>
                  <div className="calculator-input-grid">
                    <div className="form-group">
                      <label>SFM / Vc Override ({speedUnit})</label>
                      <input
                        type="number"
                        step="1"
                        value={params.sfmOverride}
                        placeholder="Use calculated baseline"
                        onChange={(event) => handleInputChange("sfmOverride", event.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label>{usesChipLoad ? `Chip Load Override (${chipUnit})` : `Feed per Rev Override (${chipUnit})`}</label>
                      <input
                        type="number"
                        step="0.0001"
                        value={params.feedOverride}
                        placeholder="Use calculated feed"
                        onChange={(event) => handleInputChange("feedOverride", event.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label>Machine Max RPM</label>
                      <input
                        type="number"
                        step="1"
                        value={params.machineMaxRPM}
                        placeholder="No cap"
                        onChange={(event) => handleInputChange("machineMaxRPM", event.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label>Machine Horsepower</label>
                      <input
                        type="number"
                        step="0.1"
                        value={params.machineHorsepower}
                        placeholder="Optional"
                        onChange={(event) => handleInputChange("machineHorsepower", event.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label>Rigidity Factor</label>
                      <input
                        type="number"
                        min="0.35"
                        max="1.25"
                        step="0.05"
                        value={params.rigidityFactor}
                        onChange={(event) => handleInputChange("rigidityFactor", event.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label>Tool Stickout ({lengthUnit})</label>
                      <input
                        type="number"
                        step="0.001"
                        value={params.stickout}
                        placeholder="Optional"
                        onChange={(event) => handleInputChange("stickout", event.target.value)}
                      />
                    </div>
                    {usesChipLoad && (
                      <div className="form-group">
                        <label>Radial Engagement ({lengthUnit})</label>
                        <input
                          type="number"
                          step="0.001"
                          value={params.radialEngagement}
                          placeholder="Optional width of cut"
                          onChange={(event) => handleInputChange("radialEngagement", event.target.value)}
                        />
                      </div>
                    )}
                    <div className="form-group">
                      <label>Coolant / Lubrication</label>
                      <select
                        value={params.coolantMode}
                        onChange={(event) => handleInputChange("coolantMode", event.target.value)}
                      >
                        <option value="flood">Flood coolant</option>
                        <option value="mist">Mist / MQL</option>
                        <option value="air">Air blast</option>
                        <option value="dry">Dry</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="form-actions" style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
              <button onClick={resetForm} className="btn-secondary" type="button">
                Reset
              </button>
            </div>
          </div>

          <div className="calculator-panel">
            {results ? (
              <>
                <div className="results-header" style={{ marginBottom: "20px" }}>
                  <h3 style={{ color: "var(--text-primary)", margin: 0 }}>Results</h3>
                </div>

                <div className="results-grid" style={resultsGridStyle}>
                  <ResultItem label="Actual Surface Speed" value={`${displaySurfaceSpeed(results.surfaceSpeed)} ${speedUnit}`} />
                  <ResultItem label={usesChipLoad ? "Actual Feed Rate" : "Calculated Feed per Revolution"} value={`${displayFeed(results.feedRate)} ${feedUnit}`} />
                  <ResultItem label="Recommended RPM" value={results.optimalRPM} />
                  <ResultItem label="Operation" value={results.operationLabel || selectedOperation?.label || "Operation"} />
                  <ResultItem label="RPM Diameter Basis" value={`${usesStockDiameter ? params.stockDiameter : params.toolDiameter} ${lengthUnit} ${results.rpmDiameterLabel || diameterLabel}`} />
                  {usesChipLoad && (
                    <ResultItem label="Recommended Feed" value={`${displayFeed(results.optimalFeed)} ${feedUnit}`} />
                  )}
                  {!usesChipLoad && (
                    <ResultItem
                      label="Feed per Minute Reference"
                      value={`${isMetric ? Math.round(results.feedPerMinute * 25.4 * 100) / 100 : results.feedPerMinute} ${isMetric ? "mm/min" : "IPM"}`}
                    />
                  )}
                  <ResultItem label="Baseline Surface Speed" value={`${displaySurfaceSpeed(results.baselineSFM)} ${speedUnit}`} />
                  <ResultItem label={usesChipLoad ? "Baseline Chip Load" : "Baseline Feed per Rev"} value={`${displayChip(results.baselineChipLoad)} ${chipUnit}`} />
                </div>

                <div style={noteBoxStyle}>
                  <strong>Industry note:</strong> These are starting values. Final speeds
                  and feeds depend on tool geometry/coating, radial and axial engagement,
                  toolholder rigidity, workholding, coolant, and machine condition.
                </div>

                {results.formulas?.length > 0 && (
                  <div style={formulaBoxStyle}>
                    <strong>Formulas used</strong>
                    <ul>
                      {results.formulas.map((formula) => (
                        <li key={formula}>{formula}</li>
                      ))}
                    </ul>
                    {results.formulaSource && (
                      <p>
                        Source: {results.formulaSource.source}. {results.formulaSource.edition}
                      </p>
                    )}
                    {isAdvanced && results.advancedAssumptions && (
                      <p>
                        Advanced trace: SFM override {results.advancedAssumptions.sfmOverride || "none"};
                        feed override {results.advancedAssumptions.feedOverride || "none"};
                        RPM cap {results.advancedAssumptions.machineMaxRPM || "none"};
                        rigidity factor {results.advancedAssumptions.rigidityFactor};
                        coolant {results.advancedAssumptions.coolantMode};
                        stickout ratio {results.advancedAssumptions.stickoutRatio ? results.advancedAssumptions.stickoutRatio.toFixed(2) : "not entered"}.
                      </p>
                    )}
                  </div>
                )}

                {results.warnings?.length > 0 && (
                  <div className="warnings" style={warningStyle}>
                    <h4 style={{ color: "var(--text-primary)", marginTop: 0 }}>Warnings</h4>
                    <ul style={{ color: "var(--text-secondary)", paddingLeft: "20px" }}>
                      {results.warnings.map((warning, index) => (
                        <li key={index}>{warning}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="results-actions" style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
                  <button onClick={handleSave} className="btn-primary" type="button">
                    Save Calculation
                  </button>
                  <button onClick={copyToClipboard} className="btn-secondary" type="button">
                    Copy
                  </button>
                </div>
              </>
            ) : (
              <div style={{ color: "var(--text-muted)", textAlign: "center", padding: "32px" }}>
                Enter valid inputs to calculate feeds and speeds.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const ResultItem = ({ label, value }) => (
  <div style={resultItemStyle}>
    <label style={resultLabelStyle}>{label}</label>
    <span style={resultValueStyle}>{value}</span>
  </div>
);

const resultsGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  gap: "12px",
  marginBottom: "20px",
};

const resultItemStyle = {
  background: "var(--bg-surface)",
  border: "1px solid var(--border-color)",
  borderRadius: "10px",
  padding: "14px",
};

const resultLabelStyle = {
  display: "block",
  color: "var(--text-muted)",
  fontSize: "14px",
  marginBottom: "6px",
};

const resultValueStyle = {
  color: "var(--text-primary)",
  fontWeight: 700,
  fontSize: "18px",
};

const noteBoxStyle = {
  background: "var(--bg-surface)",
  border: "1px solid var(--border-color)",
  borderRadius: "10px",
  padding: "14px",
  color: "var(--text-secondary)",
  lineHeight: 1.55,
  marginBottom: "20px",
};

const formulaBoxStyle = {
  background: "rgba(2, 6, 23, 0.38)",
  border: "1px solid rgba(139, 92, 246, 0.28)",
  borderRadius: "10px",
  padding: "14px",
  color: "var(--text-secondary)",
  lineHeight: 1.55,
  marginBottom: "20px",
};

const warningStyle = {
  background: "rgba(245, 158, 11, 0.12)",
  border: "1px solid rgba(245, 158, 11, 0.35)",
  borderRadius: "10px",
  padding: "16px",
  marginBottom: "20px",
};

export default FeedsSpeedsCalculator;
